// RecipePhotoStrip — top-of-page hero photo + upload control for
// the active recipe page in RecipeBook. Sits between the page
// number / title and the body. When there's no photo, renders a
// quiet "+ ADD A PHOTO" CTA in the same slot.
//
// Photos come from useRecipePhotos: a merged stream of
// recipe_photos (manual uploads) and cook_log_photos (auto from
// cooks). Tapping a thumbnail expands it; tapping the upload
// button opens the system camera/library picker.

import { useRef, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { useRecipePhotos } from "../lib/useRecipePhotos";

export default function RecipePhotoStrip({ recipeSlug, userId }) {
  const { photos, primary, uploading, uploadFromFile, error } =
    useRecipePhotos(recipeSlug, userId);
  const fileRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  const triggerUpload = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Always promote the newest upload to primary. The earlier
    // `setAsPrimary: !primary` meant a broken first upload would
    // stay as the hero forever even after the user re-uploaded —
    // there's no other UI to swap primaries. Newest wins.
    await uploadFromFile(file, { setAsPrimary: true });
    e.target.value = "";
  };

  return (
    <div style={{ width: "100%", marginBottom: 14 }}>
      {/* Hero photo OR empty-state CTA — same height so the page
          layout doesn't shift when the first photo lands. */}
      {primary ? (
        // Tap the hero to REPLACE the photo. Previously this opened
        // a lightbox; the lightbox is still reachable via the
        // thumbnail strip below when there are multiple photos.
        // Long-press also opens the lightbox so the zoom-in
        // affordance isn't entirely gone for single-photo recipes.
        <button
          type="button"
          onClick={triggerUpload}
          disabled={uploading}
          aria-label="replace photo"
          onContextMenu={(e) => {
            // Long-press on touch / right-click on desktop opens
            // the lightbox instead of triggering another upload.
            e.preventDefault();
            setLightbox(primary);
          }}
          style={{
            all: "unset",
            cursor: uploading ? "wait" : "pointer",
            display: "block",
            width: "100%",
            aspectRatio: "16 / 10",
            background: `url(${primary.url}) center / cover no-repeat`,
            borderRadius: 14,
            boxShadow: "0 4px 16px rgba(30,30,30,0.12)",
            position: "relative",
            overflow: "hidden",
            touchAction: "manipulation",
          }}
        >
          {/* Camera badge top-right — visual cue that the tile is
              tap-to-replace, not tap-to-zoom. Tapping the badge
              and tapping the photo body both trigger upload. */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 38,
              height: 38,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.92)",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 999,
              fontSize: 18,
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              pointerEvents: "none",
            }}
          >
            {uploading ? "…" : "📷"}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={triggerUpload}
          disabled={uploading}
          style={{
            all: "unset",
            cursor: uploading ? "wait" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: "100%",
            aspectRatio: "16 / 6",
            background: COLOR.deep,
            border: `1.5px dashed ${COLOR.border}`,
            borderRadius: 14,
            fontFamily: FONT.forma,
            fontSize: 14,
            fontWeight: 600,
            color: COLOR.muted,
            touchAction: "manipulation",
          }}
        >
          <span style={{ fontSize: 22 }}>📷</span>
          {uploading ? "UPLOADING…" : "+ ADD A PHOTO"}
        </button>
      )}

      {/* Thumbnail strip below — appears once there are 2+ photos
          so the user can see what's been collected. */}
      {photos.length > 1 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 8,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 4,
          }}
        >
          {photos.slice(0, 12).map((p) => (
            <button
              key={p.storage_path}
              type="button"
              onClick={() => setLightbox(p)}
              aria-label="photo thumbnail"
              style={{
                all: "unset",
                cursor: "pointer",
                flexShrink: 0,
                width: 56,
                height: 56,
                background: `url(${p.url}) center / cover no-repeat`,
                borderRadius: 8,
                border: p === primary ? `2px solid ${COLOR.leaf}` : `1px solid ${COLOR.border}`,
                touchAction: "manipulation",
              }}
            />
          ))}
          <button
            type="button"
            onClick={triggerUpload}
            disabled={uploading}
            aria-label="add another photo"
            style={{
              all: "unset",
              cursor: uploading ? "wait" : "pointer",
              flexShrink: 0,
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: COLOR.deep,
              border: `1px dashed ${COLOR.border}`,
              borderRadius: 8,
              fontSize: 20,
              color: COLOR.muted,
              touchAction: "manipulation",
            }}
          >
            +
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: 6,
            fontFamily: FONT.forma,
            fontSize: 12,
            color: COLOR.rose,
          }}
        >
          {error}
        </div>
      )}

      {/* No `capture` attribute — letting iOS render the native
          action sheet (Photo Library / Take Photo / Choose File)
          so the user can either upload an existing photo or take a
          fresh one. `capture="environment"` was forcing camera-only
          and blocking the library path entirely. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        style={{ display: "none" }}
      />

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.92)",
            zIndex: 4000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightbox.url}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              borderRadius: 12,
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      )}
    </div>
  );
}
